import 'react-spring-bottom-sheet/dist/style.css';
import 'react-toastify/dist/ReactToastify.css';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import App from './App';
import reportWebVitals from './reportWebVitals';
import store from './store';
import './styles/index.scss';
import './index.css';
import * as Sentry from '@sentry/react';
import { PostHogProvider } from 'posthog-js/react';
import { BrowserRouter } from 'react-router-dom';
import { GoogleAuthProvider } from './context/GoogleAuthContext';
import { AuthProvider } from './context/authProvider';
import { NetworkProvider } from './utils/NetworkStatusProvider';

import { ErrorLoggerFactory } from './hooks/useGlobalErrorLogger';
import { getPostHogOptions } from './posthog/getPosthogOptions';
import { openCrashReportFromUtils } from './utils/crashReportUtils';
import {
  ERROR_LEVELS,
  classifyError,
  classifyErrorByType,
  serializeError,
} from './utils/errors/errorsUtils';
import { getIds } from './utils/hooks';
import { DebugModeProvider } from './utils/useDebugMode';
import './diagnostics';
import { pwaToast } from './components/Toast';
import { isAppPWA } from './utils/helper-functions';

const cartData = localStorage.getItem('cartItems');
const userData = localStorage.getItem('user');

const cart = cartData && cartData !== 'undefined' ? JSON.parse(cartData) : null;
const user = userData && userData !== 'undefined' ? JSON.parse(userData) : null;
const isPWA = isAppPWA();

if (isPWA) {
  pwaToast(
    "🔥Une version toute neuve t'attend ! Télécharge Pikkopay sur les stores pour la meilleure expérience.🔥",
  );
}

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      networkDetailAllowUrls: [
        'https://app-webapp-hbrkedmp4q-od.a.run.app',
        ...(process.env.REACT_APP_BACKEND_URL
          ? [
              new RegExp(
                `^${'https://app-webapp-hbrkedmp4q-od.a.run.app'.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/.*`,
              ),
            ]
          : []),
        ...(process.env.NODE_ENV === 'development'
          ? [
              /^http:\/\/localhost:\d+\/.*/,
              /^https:\/\/localhost:\d+\/.*/,
              'http://localhost:3001',
              'http://localhost:8000',
            ]
          : []),

        /^https:\/\/api\.stripe\.com\/.*/,
        /^https:\/\/.*\.googleapis\.com\/.*/,
      ],
      networkRequestHeaders: ['X-Custom-Header'],
      networkResponseHeaders: ['X-Custom-Header'],
    }),
    Sentry.feedbackIntegration({
      colorScheme: 'system',
      triggerLabel: 'Signaler une erreur',
      buttonLabel: 'Feedback',
      cancelButtonLabel: 'Annuler',
      confirmButtonLabel: 'Confirmer',
      submitButtonLabel: 'Soummettre vos commentaires',
      formTitle: 'Signaler une erreur',
      addScreenshotButtonLabel: 'Ajouter un screenshot',
      removeScreenshotButtonLabel: 'Enlever un screenshot',
      nameLabel: 'Nom / prénom',
      namePlaceholder: 'Votre nom et prénom',
      emailLabel: 'Email',
      emailPlaceholder: 'email@example.com',
      messageLabel: 'Description',
      isRequiredLabel: '(requis)',
      messagePlaceholder: "Decrivez l'erreur ou le comportement inattendu ?",
      successMessageText: 'Merci pour votre retour',
    }),
  ],
  replaysSessionSampleRate: process.env.REACT_APP_REPLAYS_SESSION_SAMPLE_RATE,
  replaysOnErrorSampleRate: process.env.REACT_APP_REPLAYS_ON_ERROR_SAMPLE_RATE,
  debug: true,

  beforeSend(event) {
    const errorMsg = event.exception?.values?.[0]?.value || '';
    const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
    const hasRecaptchaInStack = frames.some((f) =>
      f.filename?.includes('recaptcha'),
    );

    if (
      errorMsg.includes("Cannot read properties of null (reading 'style')") &&
      hasRecaptchaInStack
    ) {
      return null;
    }
    return event;
  },

  // External url
  denyUrls: [
    // Tracking/analytics
    /google-analytics\.com/i,
    /googletagmanager\.com/i,
    /firebase\.com/i,
    /facebook\.net/i,

    // Broswer extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^moz-extension:\/\//i,
  ],
});

Sentry.setContext('LocalStorage', {
  user: user,
  store: JSON.parse(localStorage.getItem('store'))?.store,
  cartItems: cart,
});
Sentry.setContext('version', {
  appVersion: process.env.REACT_APP_VERSION,
});

if (cart?._id) {
  Sentry.setTag('orderId', cart._id);
}

if (user?._id) {
  Sentry.setUser({ id: user._id });
}

// Capture global JavaScript errors (outside React)
window.addEventListener('error', async (event) => {
  const { userId, guestId } = getIds();
  let isTriggerCrashReport = false;
  const errorCriticality = classifyError(event.error || event);
  let { errorType, criticality } = classifyErrorByType(event.error || event);
  const serializableError = serializeError(event?.error || event);

  const cartData = localStorage.getItem('cartItems');
  const cart =
    cartData && cartData !== 'undefined' ? JSON.parse(cartData) : null;
  const errorMsg = event.error?.message || event.message || '';
  if (
    errorMsg.includes('ChunkLoadError') &&
    /kr-asset-.+\.bundle\.js/i.test(errorMsg)
  ) {
    console.info('[Payzen] Icon asset load failed (ignored)');
    return;
  }

  // Ignore reCAPTCHA internal errors (Google script accessing unmounted DOM)
  if (
    errorMsg.includes("Cannot read properties of null (reading 'style')") &&
    (event.filename?.includes('recaptcha') ||
      event.error?.stack?.includes('recaptcha'))
  ) {
    event.preventDefault();
    return;
  }

  if (!event.error && event.message === 'Script error.') {
    Sentry.captureMessage('Cross-origin script error', {
      level: 'warning',
      tags: { errorType: 'crossOrigin' },
      extra: { filename: event.filename, lineno: event.lineno },
    });
    return;
  }

  if (errorCriticality === ERROR_LEVELS.CRITICAL) {
    isTriggerCrashReport = true;
    console.error(
      'Global JavaScript Error - isCriticalGlobalError :',
      event.error,
    );

    Sentry.withScope((scope) => {
      const storeId = JSON.parse(localStorage.getItem('store'))?.store?.id;

      scope.setTag('errorType', 'globalError');
      scope.setTag('critical', true);

      if (storeId) scope.setTag('storeId', storeId);

      scope.setLevel('fatal');
      scope.setContext('errorDetails', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        message: event.message,
      });

      const eventId = Sentry.captureException(event.error);

      openCrashReportFromUtils({
        error: event.error,
        eventId,
      });

      criticality = ERROR_LEVELS.CRITICAL;
    });
  }

  ErrorLoggerFactory.logError(
    cart?._id,
    serializableError,
    errorType,
    { userId, guestId, criticality },
    isTriggerCrashReport,
  );
});

// // Capture unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  const { userId, guestId } = getIds();
  let isTriggerCrashReport = false;
  const errorCriticality = classifyError(event.reason);
  let { errorType, criticality } = classifyErrorByType(event.reason);
  const serializableError = serializeError(event.reason);

  const cartData = localStorage.getItem('cartItems');
  const cart =
    cartData && cartData !== 'undefined' ? JSON.parse(cartData) : null;

  const reason = event.reason;
  if (
    reason?.name === 'ChunkLoadError' &&
    reason?.request?.includes('static.payzen.eu')
  ) {
    return;
  }

  const errorMessage = event.reason?.message || String(event.reason) || '';
  if (
    errorMessage.includes('ChunkLoadError') &&
    /kr-asset-.+\.bundle\.js/i.test(errorMessage)
  ) {
    console.info('[Payzen] Icon asset load failed (ignored)');
    return;
  }

  // Ignore reCAPTCHA internal errors (Google script accessing unmounted DOM)
  if (
    errorMessage.includes("Cannot read properties of null (reading 'style')") &&
    (event.reason?.stack?.includes('recaptcha') ||
      event.reason?.message?.includes('recaptcha'))
  ) {
    event.preventDefault();
    return;
  }

  if (errorCriticality === ERROR_LEVELS.CRITICAL) {
    isTriggerCrashReport = true;
    console.error(
      'Unhandled Promise Rejection - isCriticalGlobalError :',
      event.reason,
    );

    Sentry.withScope((scope) => {
      const storeId = JSON.parse(localStorage.getItem('store'))?.store?.id;

      scope.setTag('errorType', 'unhandledRejection');
      scope.setTag('critical', true);

      if (storeId) scope.setTag('storeId', storeId);
      scope.setLevel('fatal');

      const eventId = Sentry.captureException(event.reason);

      openCrashReportFromUtils({
        error: event.error,
        eventId,
      });
    });

    criticality = ERROR_LEVELS.CRITICAL;
  }

  ErrorLoggerFactory.logError(
    cart?._id,
    serializableError,
    errorType,
    { userId, guestId, criticality },
    isTriggerCrashReport,
  );
});

const posthogOptions = getPostHogOptions();

ReactDOM.render(
  <BrowserRouter>
    <AuthProvider>
      <Provider store={store}>
        {/*<GoogleReCaptchaProvider reCaptchaKey="6LfjOt0pAAAAAFcEvh1DMhOaK2d-2f5o-J3Wa_cc">*/}
        <NetworkProvider>
          <PostHogProvider
            apiKey={process.env.REACT_APP_POSTHOG_KEY}
            options={posthogOptions}
          >
            <GoogleAuthProvider>
              <DebugModeProvider>
                <App />
              </DebugModeProvider>
            </GoogleAuthProvider>
          </PostHogProvider>
        </NetworkProvider>
        {/*</GoogleReCaptchaProvider>*/}
      </Provider>
    </AuthProvider>
  </BrowserRouter>,
  document.getElementById('root'),
);

reportWebVitals();

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

    navigator.serviceWorker
      .register(swUrl)
      .then((registration) => {
        console.info(
          'Service Worker registered with scope:',
          registration.scope,
        );

        // Listen for updates to the Service Worker
        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          if (installingWorker) {
            installingWorker.onstatechange = () => {
              if (installingWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  console.info('New content is available, updating...');
                  // Notify the waiting service worker to take control immediately
                  installingWorker.postMessage({ type: 'SKIP_WAITING' });
                  setTimeout(() => {
                    window.location.reload(); // Force reload after update
                  }, 1000);
                }
              }
            };
          }
        };
      })
      .catch((error) => {
        console.error('Error during service worker registration:', error);
      });
  });
}
